import { useRef, useState } from 'react';
import { Button } from '../../../components/ui/Button.js';
import { Input } from '../../../components/ui/FormField.js';
import { Switch } from '../../../components/ui/Switch.js';
import { api } from '../../../services/api.js';
import { fromMytInput, mytInputValue } from '../../../utils/myt.js';
import { LastChanged } from '../parts/LastChanged.js';
import { SettingCard, StatusChip } from '../parts/parts.js';
import { useHomePopupStats } from '../settingsApi.js';
import { FREQUENCY_LABELS } from '../settingsFormat.js';
import type { HomePopupFrequency, HomePopupPatch, HomePopupPosterPatch } from '../settingsTypes.js';
import { HomePopupPreview } from './HomePopupPreview.js';
import type { CardProps, FieldRefusal } from './cardProps.js';

/**
 * 389 (#1305, mock-up v2 screens 4–5): Settings › App › Home pop-up. Up to five posters, each a picture with an optional
 * button and link and optional dates (Malaysia time), in staff's order; the switch; the counts of the current version;
 * a phone-size preview. Saves with the tab like every card. The platform checks every rule; a refusal's reasons are
 * placed beside the poster and field they name.
 */
const MAX_POSTERS = 5;
const LABEL_MAX = 30;
const number = new Intl.NumberFormat('en-MY');

const EMPTY: HomePopupPatch = { enabled: false, posters: [], frequency: 'every_visit' };

/** 395 (#1336, mock-up "How often"): each choice with the line under it. */
const FREQUENCY_OPTIONS: Array<{ value: HomePopupFrequency; line: string }> = [
  {
    value: 'every_visit',
    line: 'Each time a customer opens the home page, even if they closed it before.',
  },
  {
    value: 'daily',
    line: 'At most once a day on each phone. New posters show again the same day.',
  },
  { value: 'per_change', line: 'Once on each phone, until you change the posters.' },
];

export function HomePopupCard({ draft, config, latest, refusal, refusals }: CardProps) {
  const value = draft.value('home_popup') ?? EMPTY;
  const saved = draft.saved?.home_popup ?? EMPTY;
  const stats = useHomePopupStats(true);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Local previews of pictures uploaded on this page, by picture id (the upload answers no address).
  const [localImages, setLocalImages] = useState<Record<string, string>>({});

  const servedImages = Object.fromEntries(
    (config.home_popup?.posters ?? []).map((p) => [p.picture_id, p.image_url]),
  );
  const imageOf = (pictureId: string) => localImages[pictureId] ?? servedImages[pictureId] ?? null;
  const countsOf = (id: string | undefined) => stats.data?.posters.find((s) => s.id === id);

  const reasons: FieldRefusal[] = refusals?.('home_popup') ?? [];
  const reasonFor = (field: string) => reasons.find((r) => r.field === field)?.message ?? null;
  const cardLevel = reasons.filter((r) => !/^home_popup\.posters\.\d+\./.test(r.field));

  const update = (next: Partial<HomePopupPatch>) => draft.set('home_popup', { ...value, ...next });
  const updatePoster = (i: number, next: Partial<HomePopupPosterPatch>) =>
    update({ posters: value.posters.map((p, j) => (j === i ? { ...p, ...next } : p)) });
  const move = (i: number, by: -1 | 1) => {
    const list = [...value.posters];
    const [item] = list.splice(i, 1);
    if (item) list.splice(i + by, 0, item);
    update({ posters: list });
  };

  async function addPoster(file: File) {
    setUploadError(null);
    setUploading(true);
    try {
      // `file` before `purpose`: the upload route reads the purpose after the file (files/routes.ts).
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', 'home_popup');
      const asset = (await api.upload('/files/upload', fd)) as { id: string };
      setLocalImages((m) => ({ ...m, [asset.id]: URL.createObjectURL(file) }));
      update({
        posters: [
          ...value.posters,
          { picture_id: asset.id, button_label: null, link: null, starts_at: null, ends_at: null },
        ],
      });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'The picture could not be uploaded.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const dirty = draft.isDirty('home_popup');
  return (
    <SettingCard
      testId="home-popup-card"
      title="Home pop-up"
      dirty={dirty}
      refusal={reasons.length === 0 ? refusal('home_popup') : null}
      chip={
        <StatusChip testId="status-home_popup" tone={saved.enabled ? 'on' : 'off'}>
          {saved.enabled
            ? `ON · ${saved.posters.length} poster${saved.posters.length === 1 ? '' : 's'}`
            : 'OFF'}
        </StatusChip>
      }
      description={
        <p>
          Posters customers see over the home page, swiped through. Saving a change to the posters
          shows the pop-up to everyone once more.
        </p>
      }
      footer={<LastChanged setting="home_popup" latest={latest} />}
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex flex-col gap-3.5">
          {reasons.length > 0 && (
            <div
              role="alert"
              className="rounded-md border border-semantic-danger-text bg-semantic-danger-bg px-3.5 py-2.5 text-sm text-semantic-danger-text"
            >
              Not saved. Fix the fields marked below.
              {cardLevel.map((r) => (
                <span key={r.field} className="mt-1 block">
                  {r.message}
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            {stats.data?.version ? (
              <p className="text-xs text-steel">
                This version: shown <b className="text-ink">{number.format(stats.data.shown)}</b>{' '}
                times. Saving any change to the posters starts a new version.
              </p>
            ) : (
              <span />
            )}
            <span className="flex items-center gap-2.5 text-sm text-ink">
              Show to customers
              <Switch
                checked={value.enabled}
                onChange={() => update({ enabled: !value.enabled })}
                ariaLabel="Show to customers"
              />
            </span>
          </div>

          <div
            role="radiogroup"
            aria-labelledby="home-popup-frequency-heading"
            className="flex flex-col gap-1.5"
          >
            <span id="home-popup-frequency-heading" className="text-sm font-medium text-charcoal">
              How often
            </span>
            {FREQUENCY_OPTIONS.map((o) => {
              const selected = (value.frequency ?? 'every_visit') === o.value;
              return (
                <label
                  key={o.value}
                  className={[
                    'flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary',
                    selected ? 'border-primary bg-primary/5' : 'border-hairline',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="home-popup-frequency"
                    value={o.value}
                    checked={selected}
                    onChange={() => update({ frequency: o.value })}
                    className="mt-1 accent-primary"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-ink">
                      {FREQUENCY_LABELS[o.value]}
                    </span>
                    <span className="block text-xs text-steel">{o.line}</span>
                  </span>
                </label>
              );
            })}
          </div>

          {value.posters.map((p, i) => {
            const key = p.id ?? p.picture_id;
            const at = `home_popup.posters.${i}`;
            const counts = countsOf(p.id);
            const image = imageOf(p.picture_id);
            const bad = reasons.some((r) => r.field.startsWith(`${at}.`));
            const err = (suffix: string) => {
              const message = reasonFor(`${at}.${suffix}`);
              return message ? (
                <p
                  data-testid={`home-popup-error-${suffix}`}
                  className="mt-1 text-xs text-semantic-danger-text"
                >
                  {message}
                </p>
              ) : null;
            };
            return (
              <div
                key={key}
                data-testid="home-popup-poster"
                className={[
                  'grid grid-cols-[72px_minmax(0,1fr)_auto] items-start gap-3.5 rounded-lg border p-3.5',
                  bad ? 'border-semantic-danger-text bg-semantic-danger-bg/30' : 'border-hairline',
                ].join(' ')}
              >
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-bold text-charcoal">Poster {i + 1}</span>
                  {image ? (
                    <img
                      src={image}
                      alt={`Poster ${i + 1}`}
                      className="h-[90px] w-[72px] rounded-md bg-surface-feature object-cover"
                    />
                  ) : (
                    <div className="flex h-[90px] w-[72px] items-center justify-center rounded-md border border-dashed border-semantic-danger-text text-center text-[11px] text-semantic-danger-text">
                      No picture
                    </div>
                  )}
                  {err('picture_id')}
                </div>
                <div className="flex flex-col gap-2">
                  <div className="grid gap-2.5 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor={`home-popup-${i}-label`}
                        className="mb-0.5 block text-[11px] text-steel"
                      >
                        Button label
                      </label>
                      <Input
                        id={`home-popup-${i}-label`}
                        value={p.button_label ?? ''}
                        maxLength={60}
                        placeholder="No button"
                        onChange={(e) =>
                          updatePoster(i, {
                            button_label: e.target.value === '' ? null : e.target.value,
                          })
                        }
                      />
                      <p className="mt-0.5 text-[11px] text-steel">{`${(p.button_label ?? '').trim().length} / ${LABEL_MAX}`}</p>
                      {err('button_label')}
                    </div>
                    <div>
                      <label
                        htmlFor={`home-popup-${i}-link`}
                        className="mb-0.5 block text-[11px] text-steel"
                      >
                        Link
                      </label>
                      <Input
                        id={`home-popup-${i}-link`}
                        value={p.link ?? ''}
                        placeholder="https://"
                        onChange={(e) =>
                          updatePoster(i, { link: e.target.value === '' ? null : e.target.value })
                        }
                      />
                      {err('link')}
                    </div>
                  </div>
                  <div className="grid gap-2.5 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor={`home-popup-${i}-from`}
                        className="mb-0.5 block text-[11px] text-steel"
                      >
                        Show from
                      </label>
                      <Input
                        id={`home-popup-${i}-from`}
                        type="datetime-local"
                        value={mytInputValue(p.starts_at)}
                        onChange={(e) =>
                          updatePoster(i, { starts_at: fromMytInput(e.target.value) })
                        }
                      />
                      {err('starts_at')}
                    </div>
                    <div>
                      <label
                        htmlFor={`home-popup-${i}-until`}
                        className="mb-0.5 block text-[11px] text-steel"
                      >
                        Until
                      </label>
                      <Input
                        id={`home-popup-${i}-until`}
                        type="datetime-local"
                        value={mytInputValue(p.ends_at)}
                        onChange={(e) => updatePoster(i, { ends_at: fromMytInput(e.target.value) })}
                      />
                      {err('ends_at')}
                    </div>
                  </div>
                  <p className="text-[11px] text-steel">
                    Empty dates: shown any time. Malaysia time.
                  </p>
                  {counts && (
                    <p data-testid="home-popup-poster-counts" className="text-xs text-steel">
                      Seen <b className="text-ink">{number.format(counts.seen)}</b> ·{' '}
                      {p.button_label ? (
                        <>
                          button tapped <b className="text-ink">{number.format(counts.taps)}</b>
                        </>
                      ) : (
                        'no button'
                      )}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <button
                    type="button"
                    aria-label="Move up"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-hairline-strong text-charcoal disabled:opacity-40"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label="Move down"
                    disabled={i === value.posters.length - 1}
                    onClick={() => move(i, 1)}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-hairline-strong text-charcoal disabled:opacity-40"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => update({ posters: value.posters.filter((_, j) => j !== i) })}
                    className="text-[13px] font-semibold text-semantic-danger-text"
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={value.posters.length >= MAX_POSTERS || uploading}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? 'Uploading…' : '+ Add poster'}
            </Button>
            <input
              ref={fileRef}
              data-testid="home-popup-add-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void addPoster(file);
              }}
            />
            <span className="text-xs text-steel">
              <span>{`${value.posters.length} of ${MAX_POSTERS} posters`}</span>
              <span> · JPEG, PNG or WebP up to 10 MB; 4:5 fits phones best</span>
            </span>
          </div>
          {uploadError && (
            <p role="alert" className="text-sm text-semantic-danger-text">
              {uploadError}
            </p>
          )}
          {dirty && (
            <p className="text-sm font-semibold text-semantic-warning-text">
              Unsaved changes to the pop-up.
            </p>
          )}
        </div>
        <div className="flex flex-col items-center gap-2.5">
          <span className="text-xs text-steel">Preview (use the arrows to see each poster)</span>
          <HomePopupPreview
            posters={value.posters.map((p) => ({
              key: p.id ?? p.picture_id,
              image: imageOf(p.picture_id),
              button_label: p.button_label,
              link: p.link,
            }))}
          />
        </div>
      </div>
    </SettingCard>
  );
}
